import React, { useEffect, useState } from "react";
import { PhoneCall, CheckCircle2, Loader2 } from "lucide-react";
import { useT } from "../context/LanguageContext";

const LiveCallsPanel = () => {
  const t = useT();
  const SCENARIOS = [
    {
      industry: t("liveCalls.scenario1.industry"),
      action: t("liveCalls.scenario1.action"),
      duration: "2:14",
      status: t("liveCalls.scenario1.status"),
      accent: "cyan",
    },
    {
      industry: t("liveCalls.scenario2.industry"),
      action: t("liveCalls.scenario2.action"),
      duration: "1:38",
      status: t("liveCalls.scenario2.status"),
      accent: "coral",
    },
    {
      industry: t("liveCalls.scenario3.industry"),
      action: t("liveCalls.scenario3.action"),
      duration: "0:42",
      status: t("liveCalls.scenario3.status"),
      accent: "cyan",
    },
    {
      industry: t("liveCalls.scenario4.industry"),
      action: t("liveCalls.scenario4.action"),
      duration: "1:05",
      status: t("liveCalls.scenario4.status"),
      accent: "navy",
    },
    {
      industry: t("liveCalls.scenario5.industry"),
      action: t("liveCalls.scenario5.action"),
      duration: "2:51",
      status: t("liveCalls.scenario5.status"),
      accent: "coral",
    },
    {
      industry: t("liveCalls.scenario6.industry"),
      action: t("liveCalls.scenario6.action"),
      duration: "1:22",
      status: t("liveCalls.scenario6.status"),
      accent: "cyan",
    },
  ];
  const [tick, setTick] = useState(0);
  const [count, setCount] = useState(47);

  useEffect(() => {
    const t = setInterval(() => setTick((i) => i + 1), 2200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCount((c) => {
        const next = c + Math.floor(Math.random() * 5) - 2;
        return Math.max(38, Math.min(72, next));
      });
    }, 2000);
    return () => clearInterval(t);
  }, []);

  // Show 4 scenarios at once, scrolling
  const visible = [0, 1, 2, 3].map(
    (offset) => SCENARIOS[(tick + offset) % SCENARIOS.length]
  );

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "var(--bg)",
        border: "1px solid var(--border)",
      }}
    >
      {/* Header */}
      <div
        className="px-5 py-4 flex items-center justify-between"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: "#10B981",
              boxShadow: "0 0 0 4px rgba(16,185,129,0.18)",
              animation: "lcDot 1.5s ease-in-out infinite",
            }}
          />
          <span
            className="text-[11px] uppercase tracking-[0.18em] font-semibold"
            style={{ color: "var(--muted)" }}
          >
            {t("liveCalls.title")}
          </span>
        </div>
        <span
          className="text-[13px] font-bold"
          style={{ color: "var(--fg)", fontFamily: "ui-monospace, monospace" }}
        >
          {count} {t("liveCalls.active")}
        </span>
      </div>

      {/* Calls list */}
      <div className="divide-y" style={{ borderColor: "var(--border)" }}>
        {visible.map((s, i) => (
          <div
            key={`${tick}-${i}`}
            className="px-5 py-3.5 flex items-center gap-3"
            style={{
              borderBottom: "1px solid var(--border)",
              animation: i === 0 ? "lcSlide 0.5s ease" : "none",
            }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: `var(--brand-${s.accent})15`,
                color: `var(--brand-${s.accent})`,
              }}
            >
              <PhoneCall size={15} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[13.5px] font-semibold truncate"
                style={{ color: "var(--fg)" }}
              >
                {s.industry}
              </div>
              <div
                className="text-[12px] truncate"
                style={{ color: "var(--muted)" }}
              >
                {s.action}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div
                className="text-[11px] font-mono"
                style={{ color: "var(--muted)" }}
              >
                {s.duration}
              </div>
              <div
                className="text-[10.5px] inline-flex items-center gap-1 mt-0.5 font-semibold"
                style={{
                  color:
                    s.status === t("liveCalls.scenario2.status")
                      ? "var(--brand-cyan)"
                      : s.status === t("liveCalls.scenario5.status")
                      ? "var(--brand-coral)"
                      : "#10B981",
                }}
              >
                {s.status === t("liveCalls.scenario2.status") ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={10} />
                )}
                {s.status}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer waveform bar */}
      <div
        className="px-5 py-3 flex items-center gap-1.5"
        style={{ background: "var(--card-bg)" }}
      >
        <span
          className="text-[10.5px] uppercase tracking-[0.18em] font-semibold mr-2"
          style={{ color: "var(--muted)" }}
        >
          KVKK
        </span>
        {[1, 2, 3, 2, 1, 2, 3, 2, 1, 2, 1, 3].map((h, i) => (
          <span
            key={i}
            className="inline-block rounded-sm"
            style={{
              width: 3,
              height: 12,
              display: "block",
              transformOrigin: "bottom",
              background:
                i % 4 === 0 ? "var(--brand-coral)" : "var(--brand-cyan)",
              animation: `lcBar${h} ${0.9 + (i % 3) * 0.15}s ease-in-out ${
                i * 0.06
              }s infinite alternate`,
            }}
          />
        ))}
        <span
          className="ml-auto text-[10.5px] font-semibold"
          style={{ color: "var(--muted)" }}
        >
          {t("liveCalls.kvkk")}
        </span>
      </div>

      <style>{`
        @keyframes lcDot { 0%,100% { transform: scale(1); } 50% { transform: scale(1.45); } }
        @keyframes lcSlide {
          0% { transform: translateY(-8px); opacity: 0; background: rgba(31,188,232,0.06); }
          100% { transform: translateY(0); opacity: 1; background: transparent; }
        }
        @keyframes lcBar1 { 0% { transform: scaleY(0.5); } 100% { transform: scaleY(1); } }
        @keyframes lcBar2 { 0% { transform: scaleY(0.7); } 100% { transform: scaleY(1.15); } }
        @keyframes lcBar3 { 0% { transform: scaleY(0.9); } 100% { transform: scaleY(1.35); } }
      `}</style>
    </div>
  );
};

export default LiveCallsPanel;
